import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  CheckboxFilterTree,
  RadioFilterTree,
  toast,
} from "@jana-technology/platform-ui";

import { LoadingSpinner } from "components/loaders";
import {
  deriveRadioTreeBreadcrumbs,
  extractWellNameFromRadioValue,
  flattenRadioState,
  generateRadioState,
  useGlobalFiltersContext,
} from "contexts/filters";
import { RISK_ASSET_FILTERS_NO_RESULTS_MESSAGE } from "constants/charts";

import {
  RiskAssetFilters,
  SidebarComponentHeading,
  SidebarSpacer,
} from "./components";
import { HierarchyIdToaster } from "./HierarchyIdToaster";

export const RiskAssetSelection = ({
  hasWellSelection,
}: {
  hasWellSelection?: boolean;
}) => {
  const params = useParams<{
    wellIdName?: string;
  }>();

  const {
    selectedHierarchyIds,
    setSelectedHierarchyIds,
    hierarchyCheckboxItems,
    updateHierarchyIds,
    radioState,
    setRadioState,
    onRadioChange,
    selectedWellName,
    setSelectedWellName,
    setAppliedWell,
    hierarchiesWithWellNamesRadios,
    selectedRiskAssetFilters,
    setSelectedRiskAssetFilters,
    savedRiskAssetFilters,
    setSavedRiskAssetFilters,
    activeFilterCount,
    assetSelectionQueryResult,
    assetSelectionFilterQueryResult,
    wellNamesByHierarchyQueryResult,
    wellIdNameParam,
  } = useGlobalFiltersContext();

  const handleRadioUpdate = useCallback(
    (hierarchyId: string, wellName: string) => {
      const radioTreeBreadcrumbs = deriveRadioTreeBreadcrumbs(
        hierarchiesWithWellNamesRadios,
        hierarchyId,
        `${hierarchyId}_${wellName}`,
      );

      setSelectedWellName(radioTreeBreadcrumbs.at(-1)!);
      setRadioState(generateRadioState(radioTreeBreadcrumbs));
    },
    [hierarchiesWithWellNamesRadios, setRadioState, setSelectedWellName],
  );

  const refireNoResultsWarning = (
    _event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    hasFilterChanged: boolean,
  ) => {
    if (hasFilterChanged && wellNamesGroupedByHierarchy?.length === 0) {
      toast.warning(RISK_ASSET_FILTERS_NO_RESULTS_MESSAGE);
    }
  };

  const [filtersOpened, setFiltersOpened] = useState(false);

  const filtersLoaded =
    assetSelectionFilterQueryResult.dataState === "complete";

  const { wellNamesGroupedByHierarchy } =
    wellNamesByHierarchyQueryResult.data ?? {};

  // init well from route param (routing happens in RiskManagementCharts)
  useEffect(() => {
    if (wellNamesGroupedByHierarchy && wellNamesGroupedByHierarchy.length > 0) {
      const getFoundIdAndWellName = (wellIdName: string) => {
        const [hierarchyId, wellName] = wellIdName.split("_");

        const coercedHierarchyId = Number(hierarchyId);

        // find the hierarchyId and wellName in the data
        const hierarchyIdNode = wellNamesGroupedByHierarchy
          .filter(Boolean)
          .find((hierarchy) => hierarchy.hierarchyId === coercedHierarchyId);

        const foundWellName =
          hierarchyIdNode?.wellNames?.find((name) => name === wellName) ?? "";

        return [coercedHierarchyId, foundWellName] as const;
      };

      if (wellIdNameParam) {
        const [appliedHierarchyId, appliedWellName] =
          getFoundIdAndWellName(wellIdNameParam);

        if (appliedWellName) {
          return handleRadioUpdate(String(appliedHierarchyId), appliedWellName);
        }
      }

      const [decodedHierarchyId, decodedWellName] = getFoundIdAndWellName(
        decodeURIComponent(params.wellIdName ?? "_"),
      );

      if (decodedWellName) {
        setAppliedWell((state) => {
          if (state[0] !== decodedHierarchyId || state[1] !== decodedWellName) {
            return [decodedHierarchyId, decodedWellName];
          }

          return state;
        });

        handleRadioUpdate(String(decodedHierarchyId), decodedWellName);
      } else {
        // if the param (id_wellName) is invalid, set the first well
        const [defaultHierarchy] = wellNamesGroupedByHierarchy;

        const { hierarchyId = 0, wellNames = [] } = defaultHierarchy ?? {};
        const defaultWellName = wellNames?.[0] ?? "";

        if (defaultWellName) {
          setAppliedWell([Number(hierarchyId), defaultWellName]);

          handleRadioUpdate(String(hierarchyId), defaultWellName);
        } else {
          throw new Error("No well names found for any hierarchy");
        }
      }
    }
  }, [
    wellNamesGroupedByHierarchy,
    setAppliedWell,
    setSelectedWellName,
    onRadioChange,
    handleRadioUpdate,
    params.wellIdName,
    wellIdNameParam,
  ]);

  const filterSettingsButton = filtersLoaded ? (
    <RiskAssetFilters
      open={filtersOpened}
      activeFilterCount={activeFilterCount}
      baseFilters={assetSelectionFilterQueryResult.data?.assetSelectionFilter}
      onOpenChange={setFiltersOpened}
      selectedRiskAssetFilters={selectedRiskAssetFilters}
      savedRiskAssetFilters={savedRiskAssetFilters}
      setSelectedRiskAssetFilters={setSelectedRiskAssetFilters}
      setSavedRiskAssetFilters={setSavedRiskAssetFilters}
      onApplyButtonClick={refireNoResultsWarning}
    />
  ) : undefined;

  const hierarchyIdsKeys = Object.keys(selectedHierarchyIds);
  const submitDisabled = hierarchyIdsKeys.every(
    (key) => selectedHierarchyIds[key] === false,
  );

  const { flattenedRadioState, expandedRadios } = useMemo(() => {
    const flattenedRadioState = flattenRadioState(radioState);

    return {
      flattenedRadioState,
      expandedRadios: flattenedRadioState.map((item) => item.value),
    };
  }, [radioState]);

  return (
    <HierarchyIdToaster
      selectedHierarchyIdsLength={hierarchyIdsKeys.length}
      submitDisabled={submitDisabled}
      wellNamesGroupedByHierarchy={wellNamesGroupedByHierarchy}
    >
      <SidebarSpacer>
        <SidebarComponentHeading>Asset Selection:</SidebarComponentHeading>
        <div>
          <LoadingSpinner
            loading={assetSelectionQueryResult.loading}
            className="h-[79px] w-[280px]"
          >
            {hasWellSelection ? (
              <RadioFilterTree
                key={String(expandedRadios.length > 0)}
                showSearch
                filterSettingsButton={filterSettingsButton}
                radioState={radioState}
                onRadioChange={onRadioChange}
                buttonText="Apply Risk Filters"
                fullFilterTree={hierarchiesWithWellNamesRadios}
                filters={hierarchiesWithWellNamesRadios}
                submitButtonOnClick={() => {
                  if (selectedWellName) {
                    const wellNode = flattenedRadioState.find(
                      (node) => node.value === selectedWellName.value,
                    );

                    if (wellNode) {
                      // if we found the well node, it's parent should be the hierarchy node
                      const hierarchyNode =
                        flattenedRadioState[
                          flattenedRadioState.indexOf(wellNode) - 1
                        ];

                      if (hierarchyNode) {
                        setAppliedWell([
                          Number(hierarchyNode.value),
                          extractWellNameFromRadioValue(wellNode.value),
                        ]);
                      }
                    }
                  }
                }}
                defaultExpandedValues={expandedRadios}
              />
            ) : (
              hierarchyIdsKeys.length > 0 && (
                <CheckboxFilterTree
                  checkedState={selectedHierarchyIds}
                  submitDisabled={submitDisabled}
                  submitButtonOnClick={() => {
                    updateHierarchyIds(selectedHierarchyIds);
                  }}
                  onCheckedChange={setSelectedHierarchyIds}
                  buttonText="Apply Risk Filters"
                  fullFilterTree={hierarchyCheckboxItems}
                  filters={hierarchyCheckboxItems}
                  defaultExpandedValues={Object.keys(selectedHierarchyIds)}
                />
              )
            )}
          </LoadingSpinner>
        </div>
      </SidebarSpacer>
    </HierarchyIdToaster>
  );
};

export default RiskAssetSelection;
